import { Typography, Tooltip } from '@mui/material';
import { getStatusChipCss } from '../themes/generalTheme';
import { toCapitalizeFromUnderScore } from './helpers';
import SmartTooltipChip from '../../components/SmartTooltipChip';

export const Goals = (value, displayType = 'line', className) => {
  return value ? (
    <>
      {displayType === 'line' ? (
        <Tooltip
          title={getGoalLabels(value).join(', ')}
          arrow
          placement="top"
          enterTouchDelay={0}
          enterDelay={100}
        >
          <Typography noWrap>{getGoalLabels(value).join(', ')}</Typography>
        </Tooltip>
      ) : (
        getGoalLabels(value).map((goal) => (
          <SmartTooltipChip
            key={goal}
            label={typeof goal === 'string' ? goal.replace(/_/g, ' ') : goal}
            style={getStatusChipCss()}
            className={className}
          />
        ))
      )}
    </>
  ) : (
    ''
  );
};

export const getHealthGoals = (idealGoals, displayType = 'line', className) => {
  // Handle case where idealGoals is a string (single goal)
  if (typeof idealGoals === 'string') {
    return Goals([idealGoals], displayType, className);
  }

  // Handle case where idealGoals is an object with for_health array
  const { for_health = [] } = idealGoals || {};
  return Goals(
    for_health.filter((goal) => goal !== 'ManageWeight'),
    displayType,
    className,
  );
};

export const getGoals = (idealGoals, displayType = 'line', className) => {
  const { for_health = [], weight_goal, goal_diet } = idealGoals || {};
  return Goals(
    [
      ...for_health.filter((goal) => goal !== 'ManageWeight'),
      ...(for_health.includes('ManageWeight') && weight_goal ? [`${weight_goal} weight`] : []),
      ...(goal_diet ? [`${dietCodesToNames[goal_diet] || goal_diet} diet`] : []),
    ],
    displayType,
    className,
  );
};

export const getGoalLabels = (goals) =>
  goals.map((goal) => {
    if (goalLabels[goal]) {
      return goalLabels[goal];
    } else if (goal.includes('other:')) {
      return goal.replace('other:', '');
    } else {
      return toCapitalizeFromUnderScore(goal);
    }
  });

// - replace this with /api/v3/goals/all
const goalLabels = {
  optimize_immunity: 'Immunity support',
  prevent_control_diabetes: 'Prevent and control diabetes',
  manage_heart_failure: 'Manage heart failure',
  improve_overall: 'Improve overall health',
  reduce_cardio_risk: 'Reduce cardiovascular disease risk',
  prevent_diabetes: 'Prevent Diabetes',
  control_diabetes: 'Control Diabetes',
  control_bp: 'Control Blood Pressure',
  decrease_inflammation: 'Decrease Inflammation',
  manage_high_cholesterol: 'Manage High Cholesterol',
  manage_food_sensitivities: 'Manage Food Sensitivities',
  improve_gut_health: 'Improve Gut Health',
};

// - replace this with /api/v3/diets/all
export const dietCodesToNames = {
  PAL: 'Paleo',
  KET: 'Keto',
  LOC: 'Low-Carb',
  SOU: 'Southern',
  AME: 'American',
  LOF: 'Low-Fat',
  TLC: 'TLC',
  AHA: 'AHA Heart Healthy',
  DSH: 'DASH',
  MND: 'MIND',
  MED: 'Mediterranean',
  NRM: 'No Red Meat',
  FLX: 'Flexitarian',
  PES: 'Pescatarian',
  VET: 'Vegetarian',
  VEG: 'Vegan',
  MEX: 'Mexican',
  SA: 'South Asian',
  SA_V: 'South Asian Vegetarian',
};

/**
 * Helper function to retrieve the nutrient profile data
 *
 * @param {Array} nutrientsData - Array of nutrient objects
 * @returns {Array} - Array of nutrient objects
 */
export const getNutrientProfile = (nutrientsData) => {
  const keysToFind = [
    'energy-kilocalories',
    'total-carbohydrate',
    'total-fat',
    'total-saturated-fatty-acids-sfa',
    'sodium',
    'total-protein',
    'total-dietary-fiber',
    'added-sugars-by-total-sugars',
    'net-available-carbs',
  ];

  // Return an ordered array instead of an object
  const orderedNutrients = [];

  if (nutrientsData) {
    // Create a map for easy lookup
    const nutritionInfoMap = {};
    nutrientsData.forEach((info) => {
      nutritionInfoMap[info.key] = info;
    });

    // Process keys in the order they appear in keysToFind
    keysToFind.forEach((key) => {
      const info = nutritionInfoMap[key];
      if (info) {
        orderedNutrients.push({
          key,
          id: key
            .split('-')
            .map((word, index) => {
              if (index === 0) return word;
              return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(''),
          name: info.label.trim(),
          value: `${Math.round(info.value)} ${info.unit}`,
        });
      }
    });
  }
  return orderedNutrients;
};
